/** @jsx jsx */

import { Fragment, useState } from 'react';
import { camelCase, filter, get, isEmpty, join, pick } from 'lodash';

import { css, jsx } from '@emotion/core';

import CheckboxField from 'app/common/components/CheckboxField';
import DropdownInputField from 'app/developer-v3/components/DropdownInputField';
import FieldMessage, {
  FieldType,
} from 'app/developer-v3/components/FieldMessage';
import FlowForm from 'app/common/components/FlowForm';
import FormaticConfig from 'app/developer-v3/FormaticConfig';
import SelectSearchOutputField from 'app/developer-v3/components/SelectSearchOutputField';
import { FIELD_KEYS, NEW_SERVICE_FIELD } from 'app/developer-v3/constants';
import {
  getInputField,
  normalizeField,
  normalizeFormFields,
} from 'app/developer-v3/utils';

import type { ErrorList } from 'app/developer-v3/types/validator';
import type {
  ActionInputParams,
  ActionSchema,
  DropdownType,
  FormFieldTemplate,
  FormifiedFieldSchema,
  InfoMeta,
} from 'app/developer-v3/types';
import type { StaticDropdown } from 'app/developer-v3/platformSchema/field';
import type { TriggerSchema } from 'app/developer-v3/platformSchema/trigger';

const Styles = {
  checkbox: css`
    margin-bottom: 10px;
  `,
  dropdown: css`
    margin-top: 30px;
  `,
};

type Props = {
  action: ActionSchema;
  attemptedSave: boolean;
  errors: ErrorList;
  formFields: FormFieldTemplate[];
  formifiedField: FormifiedFieldSchema;
  isFormValid: boolean;
  info?: InfoMeta;
  onBlur: (options: { field: { value: string; key: string } }) => void;
  onFormChange: (formValues: FormifiedFieldSchema) => void;
  params: ActionInputParams;
  searches: ActionSchema[];
  setDropdown: (type: DropdownType, dropdown: string | StaticDropdown) => void;
  setSearch: (type: DropdownType, searchRef: string) => void;
  toggleDropdown: (isDropdownEnabled: boolean) => void;
  toggleSearch: (isDropdownEnabled: boolean) => void;
  availableTriggers: TriggerSchema[];
};

const ActionInput = (props: Props) => {
  const {
    action,
    attemptedSave,
    errors,
    formFields,
    formifiedField,
    isFormValid,
    info,
    onBlur,
    onFormChange,
    params,
    searches,
    setDropdown,
    setSearch,
    availableTriggers,
    toggleDropdown,
    toggleSearch,
  } = props;
  const { actionType, childInputKey: childInputIndex, inputKey } = params;

  const inputField = getInputField(action, inputKey);
  // These fields are missing in DynanicInputField which is fine since they'll be falsy.
  const { choices, dynamic, search } = childInputIndex
    ? get(inputField, ['children', childInputIndex], {})
    : inputField;

  const [dropdownType, setDropdownType] = useState(
    dynamic ? FIELD_KEYS.dynamic : FIELD_KEYS.choices
  );
  const [isDropdownEnabled, enableDropdown] = useState(
    inputKey === NEW_SERVICE_FIELD ? false : !!choices || !!dynamic
  );
  const [isSearchEnabled, enableSearch] = useState(!!search);

  const onChangeDropdownType = (type: string) => {
    setDropdownType(type);
    enableSearch(type === 'choices' ? false : isSearchEnabled);
  };

  const onCheckDropdown = isChecked => {
    enableDropdown(isChecked);
    toggleDropdown(isChecked);
  };

  const onCheckSearch = isChecked => {
    enableSearch(isChecked);
    toggleSearch(isChecked);
  };

  const formValues = normalizeField(formifiedField);
  const dropdownConfigs = pick(
    isEmpty(formValues) ? getInputField(action, inputKey) : formValues,
    [FIELD_KEYS.dynamic, FIELD_KEYS.choices]
  );
  const [source, name, label] = get(formValues, ['search'], '').split('.');
  const searchValue = { source, name, label };

  return (
    <Fragment>
      <FlowForm
        config={FormaticConfig}
        fields={normalizeFormFields(formFields, formValues)}
        isValid={isFormValid}
        meta={{ errors: attemptedSave && errors, info }}
        onBlur={onBlur}
        onChange={onFormChange}
        value={formValues}
      />
      <CheckboxField
        css={Styles.checkbox}
        field={{
          key: 'isDropdownEnabled',
          label: 'Dropdown',
          type: 'boolean-input',
          value: isDropdownEnabled,
        }}
        onChange={onCheckDropdown}
      />
      {info && info.dynamic && (
        <FieldMessage
          fieldKey="is-dropdown-enabled"
          message={info.dynamic.message}
          type={camelCase(`info ${info.dynamic.type}`) as FieldType}
        />
      )}
      <div css={Styles.dropdown}>
        {isDropdownEnabled && (
          <DropdownInputField
            addsSearchfill={!!actionType}
            dropdownConfigs={dropdownConfigs}
            errors={attemptedSave && errors}
            info={info}
            onChangeType={onChangeDropdownType}
            setDropdown={setDropdown}
            triggers={availableTriggers}
          />
        )}
        {dropdownType === 'dynamic' && !!actionType && dropdownConfigs.dynamic && (
          <Fragment>
            <CheckboxField
              css={Styles.checkbox}
              field={{
                key: 'isSearchEnabled',
                label: 'Add a search to this field',
                value: isSearchEnabled,
              }}
              onChange={onCheckSearch}
            />
            {isSearchEnabled && (
              <SelectSearchOutputField
                errors={attemptedSave && errors}
                info={info}
                onChange={value =>
                  setSearch(
                    FIELD_KEYS.dynamic,
                    join(filter([value.source, value.name]), '.')
                  )
                }
                searches={searches}
                value={searchValue}
              />
            )}
          </Fragment>
        )}
      </div>
    </Fragment>
  );
};

export default ActionInput;
